import { useEffect, useState, useRef, useMemo } from 'react';

import queryString from 'query-string';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';

import { routes } from 'lane-shared/config';
import { useSignUpContext } from 'lane-shared/contexts/SignUpContext';
import { LoginNotExistsError } from 'activate-errors';
import { isThisError } from 'lane-shared/helpers';
import { oAuthConfigScheme } from 'lane-shared/helpers/oAuth';
import { UserLoginProviderEnum } from 'constants-user';

import OAuthService from '../../utils/oauth/OAuth.service';
import { decompressData } from './helpers';
import { signInOAuth } from './signInOAuth';

type QueryStringParams = {
  [k: string]: string | (string | null)[] | null | undefined;
};

function isUserLoginProviderEnum(t: any): t is UserLoginProviderEnum {
  return Object.values(UserLoginProviderEnum).includes(t);
}

const redirectResponseValidator = yup
  .object()
  .shape({
    code: yup.string().trim().required(),
    secret: yup.string().trim().optional(),
    id_token: yup.string().trim().optional(),
    user: yup.string().trim().optional(),
  })
  .required()
  .noUnknown();

function _getRedirectResponseFromParams(params: QueryStringParams) {
  return redirectResponseValidator.validateSync(params);
}

function formatUserFullName(user: string) {
  const { name } = JSON.parse(user);
  const formattedName = `${name.firstName} ${name.lastName}`;

  return formattedName;
}

function handleLoginNotExistsError(context: {
  provider: string;
  redirectResponse: QueryStringParams;
  config: any;
  response: any;
  addOAuthSignUpDetails: any;
  history: any;
}) {
  const {
    provider,
    redirectResponse,
    config,
    response,
    addOAuthSignUpDetails,
    history,
  } = context;

  if (!isUserLoginProviderEnum(provider)) {
    throw new Error(`OAuth2 provider "${provider}" is not valid.`);
  }

  // In the case of Apple SSO only, a user object is returned the first time
  // the user signs up.
  // user: {name: { firstName: string, lastName: string}, email: string}
  const user = redirectResponse.user;
  const options: {
    inviteId?: string | null;
    fullName?: string;
  } = {
    inviteId: config.inviteId,
  };

  if (user && typeof user === 'string') {
    const userFullName = formatUserFullName(user);

    options.fullName = userFullName;
  }

  addOAuthSignUpDetails(response.idToken, provider, options);
  history.push(routes.signUpCompany);
}

export function useOAuth2Params(location: Location = window.location) {
  const oAuthServiceLock = useRef<boolean>(false);
  const { addOAuthSignUpDetails } = useSignUpContext();
  const [error, setError] = useState<undefined | string>(undefined);

  const history = useHistory();

  // Parse location parameters once and memoize them
  const params = useMemo(() => {
    const hashString = queryString.parse(location.hash);
    const searchString = queryString.parse(location.search);

    return {
      ...hashString,
      ...searchString,
    };
  }, [location.hash, location.search]);

  useEffect(() => {
    async function handleOAuth2Params(state: string) {
      const decompressedState = await decompressData(state);

      const config = oAuthConfigScheme.required().validateSync({
        clientSecret: params.secret,
        ...decompressedState,
      });

      if (!decompressedState.codeVerifier) {
        throw new Error('No code verifier.');
      }

      const provider = config.provider;

      if (!provider || typeof provider !== 'string') {
        throw new Error('Missing OAuth2 provider.');
      }

      const redirectResponse = _getRedirectResponseFromParams(params);

      const oAuthService = new OAuthService({
        config,
        codeVerifier: decompressedState.codeVerifier,
      });

      const response =
        await oAuthService.authorizeFromRedirectResponse(redirectResponse);

      try {
        await signInOAuth(response.idToken, provider, config.inviteId);
      } catch (err) {
        if (isThisError(err, LoginNotExistsError)) {
          handleLoginNotExistsError({
            provider,
            redirectResponse,
            config,
            response,
            addOAuthSignUpDetails,
            history,
          });

          return;
        }

        history.push(routes.signUp);
      }
    }

    if (oAuthServiceLock.current) {
      return;
    }

    const state = params.state;

    if (!state || typeof state !== 'string') {
      return;
    }

    oAuthServiceLock.current = true;

    handleOAuth2Params(state).catch(err => {
      setError(err.message);
    });
    // Note: We intentionally don't include 'params' in the dependency array to avoid
    // the bug where location object reference doesn't change in tests, preventing
    // the effect from re-running when location.hash or location.search change.
    // Instead, we depend on the specific properties that matter for the OAuth flow.
  }, [
    params.state,
    params.secret,
    params.code,
    params.id_token,
    params.user,
    history,
    addOAuthSignUpDetails,
  ]);

  return { ...params, error };
}

export default useOAuth2Params;
